import { useState, useMemo } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material"
import {
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"
import SettlementReportGenerator from "./SettlementReportGenerator"
import ClientBalanceCard from "../../components/ClientBalanceCard"

function StatCard({ title, value, icon, color = "primary", subtitle, loading = false }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ flex: 1 }}>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="h4" component="h2" fontWeight="bold">
                  {value}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box
            sx={{
              color: `${color}.main`,
              bgcolor: `${color}.light`,
              p: 1.5,
              borderRadius: 2,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

function FeeSettlerDashboard() {
  const navigate = useNavigate()
  const [dateFilter, setDateFilter] = useState("today")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useApiQuery(["settlement-stats", dateFilter], `/settlements/stats?filter=${dateFilter}`)

  const {
    data: settlements,
    isLoading: settlementsLoading,
    refetch: refetchSettlements,
  } = useApiQuery(["settlements", dateFilter], `/settlements?filter=${dateFilter}`)

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  const handleRefresh = () => {
    refetchStats()
    refetchSettlements()
  }

  // Get recent settlements (last 10)
  const recentSettlements = useMemo(() => {
    if (!settlements) return []
    return settlements.slice(0, 10)
  }, [settlements])

  if (statsLoading || settlementsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (statsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading dashboard data: {statsError.message}
      </Alert>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Fee Settlement Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage and track fee settlements for completed jobs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/settle-fees")}
            size="large"
            sx={{
              backgroundColor: "#10b981",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}
          >
            Settle Job Fees
          </Button>
        </Stack>
      </Box>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon color="action" />
          <Typography variant="body1" fontWeight={600}>
            Filter Period:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={dateFilter} label="Time Period" onChange={(e) => setDateFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Showing data for: <strong>{getFilterLabel()}</strong>
          </Typography>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Settlements"
            value={stats?.totalSettlements?.toLocaleString() || "0"}
            icon={<CheckCircleIcon sx={{ fontSize: 32 }} />}
            color="success"
            subtitle={`${stats?.settlementsInPeriod || 0} ${getFilterLabel().toLowerCase()}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Amount Settled"
            value={`₹${(stats?.totalAmountSettled || 0).toLocaleString()}`}
            icon={<TrendingUpIcon sx={{ fontSize: 32 }} />}
            color="primary"
            subtitle={getFilterLabel()}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}> 
          <StatCard
            title="Current Client Balance"
            value={`₹${(stats?.totalBalanceCarried || 0).toLocaleString()}`}
            icon={<AccountBalanceIcon sx={{ fontSize: 32 }} />}
            color="warning"
            subtitle={"Total available client balance as of now."}
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={3}>
          <ClientBalanceCard stats={stats} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Settlements"
            value={stats?.settlementsInPeriod || 0}
            icon={<AssignmentIcon sx={{ fontSize: 32 }} />}
            color="info"
            subtitle={getFilterLabel()}
          />
        </Grid>
      </Grid>

      {/* Recent Settlements */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Recent Settlements
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate("/all-settlements")}
              >
                View All
              </Button>
            </Box>

            {recentSettlements.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No settlements found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {dateFilter !== "all"
                    ? `No settlements for ${getFilterLabel().toLowerCase()}. Try changing the filter.`
                    : "Start by settling job fees."}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/settle-fees")}
                  sx={{ mt: 2 }}
                >
                  Settle Job Fees
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Client</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Job ID</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Amount Used</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Balance Carried</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Settled By</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentSettlements.map((settlement) => (
                      <TableRow key={settlement._id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(settlement.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(settlement.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {settlement.clientId?.name || "N/A"}
                          </Typography>
                          {settlement.clientId?.phoneNumber && (
                            <Typography variant="caption" color="textSecondary">
                              {settlement.clientId.phoneNumber}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={settlement.jobId || "N/A"}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ₹{(settlement.totalUsed || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="warning.main">
                            ₹{(settlement.balanceCarried || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {settlement.settledBy?.name || "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 2 }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                  }}
                  onClick={() => navigate("/settle-fees")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
                    <Typography variant="h6">Settle Job Fees</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Process fee settlements
                    </Typography>
                    <Chip label="Primary Action" size="small" color="success" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                  }}
                  onClick={() => navigate("/all-settlements")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <AssignmentIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                    <Typography variant="h6">View All Settlements</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Browse settlement history
                    </Typography>
                    <Chip
                      label={`${stats?.totalSettlements || 0} Total`}
                      size="small"
                      color="primary"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                  }}
                  onClick={() => navigate("/all-jobs")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: "info.main", mb: 1 }} />
                    <Typography variant="h6">Pending Jobs</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Jobs ready for settlement
                    </Typography>
                    <Chip label="View Jobs" size="small" color="info" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid> */}

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                  }}
                  onClick={() => setReportDialogOpen(true)}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
                    <Typography variant="h6">Settlement Report</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Generate reports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Financial Summary */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Financial Summary ({getFilterLabel()})
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Total Amount Settled:
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  ₹{(stats?.totalAmountSettled || 0).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Balance Carried Forward:
                </Typography>
                <Typography variant="h6" color="warning.main" fontWeight="bold">
                  ₹{(stats?.totalBalanceCarried || 0).toLocaleString()}
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Settlements Processed:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {stats?.settlementsInPeriod || 0}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Average Settlement Amount:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  ₹
                  {stats?.settlementsInPeriod > 0
                    ? Math.round(
                        (stats?.totalAmountSettled || 0) / (stats?.settlementsInPeriod || 1)
                      ).toLocaleString()
                    : 0}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid> */}

        {/* Activity Summary */}
        {/* <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              Activity Summary
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Total Settlements (All Time):
                </Typography>
                <Chip label={stats?.totalSettlements || 0} color="success" />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Settlements ({getFilterLabel()}):
                </Typography>
                <Chip label={stats?.settlementsInPeriod || 0} color="primary" />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body1" color="textSecondary">
                  Recent Activity:
                </Typography>
                <Chip
                  label={`${recentSettlements.length} records`}
                  color="info"
                />
              </Box>
              <Divider />
              <Box sx={{ textAlign: "center", pt: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  Keep processing settlements to maintain up-to-date records
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid> */}
      </Grid>
      <SettlementReportGenerator 
        open={reportDialogOpen} 
        onClose={() => setReportDialogOpen(false)} 
      />
    </Box>
  )
}

export default FeeSettlerDashboard